import { useState } from "react";
import { Clock, Info, Mountain, Plus, Snowflake, X } from "lucide-react";
import { TAG_COLOR } from "../lib/theme";
import { dictionaries } from "../lib/i18n";
import { formatHours } from "../lib/format";
import type { UiLang, Stop } from "../lib/types";

interface Props {
  /** Stopp som ikke er i ruten, sortert langs korridoren. */
  alternatives: Stop[];
  /** Stoppene i dagen, brukt til å finne hva som ligger i nærheten. */
  dayStops: Stop[];
  onAdd: (stopId: string) => void;
  /**
   * Vis stedet på musepekeren. Se StopHoverCard.
   *
   * Uten dette var «legg til et stopp» en liste over stedsnavn brukeren ikke
   * hadde noen grunn til å kjenne — se kommentaren over komponenten.
   */
  onHover?: (stop: Stop | null) => void;
  /** Åpne detaljpanelet for stedet, som er svaret der pekeren ikke finnes. */
  onInspect?: (stop: Stop) => void;
  lang: UiLang;
}

/** Hvor langt fra dagens strekning vi regner et stopp som «i nærheten». */
const NEARBY_MARGIN = 0.08;

/**
 * Lar brukeren legge et stopp inn i ruten selv — designdokumentet §2,
 * «justere ved behov».
 *
 * Forslagene begrenses til stopp som ligger langs dagens strekning, slik at
 * et tillegg ikke sender deg tilbake østover. Hele lista er ett klikk unna.
 *
 * ── Hvorfor raden viser mer enn navnet ─────────────────────────────────────
 *
 * Lista var navn, høyde og timer fra start. For en nordmann er «Undredal» nok;
 * for alle andre er det en streng med bokstaver. Appen ba altså brukeren velge
 * mellom tolv steder den ikke hadde fortalt noe om — og den *hadde* fortalt
 * noe om dem alle andre steder i grensesnittet: hvert eneste alternativ kommer
 * fra serveren med bilde, beskrivelse, tagg, anbefalt tid, fottur og
 * vintertilbud. Dataene lå i props og ble ikke vist.
 *
 * Nå gjør de tre tingene:
 *
 *   – **Typen står på raden.** Fjell, fjord eller by, som farget brikke — det
 *     er det raskeste svaret på «hva er dette stedet», og det kan leses ved å
 *     skumme framfor å lese.
 *   – **Bildet og beskrivelsen kommer på hover.** Samme kort som kartet og
 *     tidslinja bruker, så et sted man vurderer å legge inn ser ut som et sted
 *     man har lagt inn. Raden er merket `data-hover-source`, som er det kortet
 *     krever for å holde seg åpent — se StopHoverCard.
 *   – **En «i»-knapp åpner detaljpanelet.** Hover finnes ikke på en telefon,
 *     og det er der en tilreisende sitter når de planlegger fra hotellet.
 */
export function AddStopPicker({
  alternatives,
  dayStops,
  onAdd,
  onHover,
  onInspect,
  lang,
}: Props) {
  const t = dictionaries[lang];
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);

  if (alternatives.length === 0) return null;

  const from = dayStops.at(0)?.position ?? 0;
  const to = dayStops.at(-1)?.position ?? 1;

  const nearby = alternatives.filter(
    (stop) =>
      stop.position >= from - NEARBY_MARGIN && stop.position <= to + NEARBY_MARGIN,
  );
  const shown = showAll || nearby.length === 0 ? alternatives : nearby;

  if (!open) {
    return (
      <button className="add-stop-trigger no-print" onClick={() => setOpen(true)}>
        <Plus size={14} />
        {t.addStop}
      </button>
    );
  }

  const close = () => {
    setOpen(false);
    setShowAll(false);
    // Kortet skal ikke bli stående når lista det hørte til forsvinner.
    onHover?.(null);
  };

  return (
    <div className="add-stop no-print">
      <div className="add-stop-head">
        <span>
          {showAll || nearby.length === 0
            ? t.addStopAll(alternatives.length)
            : t.addStopNearby(nearby.length)}
        </span>
        <button
          className="drawer-close"
          style={{ float: "none", margin: 0 }}
          onClick={close}
          aria-label={t.close}
        >
          <X size={16} />
        </button>
      </div>

      {/* Sier hva hover gir, så det ikke bare er noe man oppdager tilfeldig. */}
      <p className="add-stop-hint">{t.addStopHint}</p>

      <div className="add-stop-list">
        {shown.map((stop) => {
          /*
           * Vinterlinja er med her og ikke bare i detaljpanelet fordi den kan
           * være hele svaret: et sted som er vinterstengt skal man vite om før
           * man legger det inn, ikke etter. Serveren har allerede filtrert de
           * sesongstengte ut av alternativene i vintermånedene, så det som står
           * igjen er «krevende» og «for erfarne» — og det er verdt å lese.
           */
          const winterNote = stop.winter?.access;
          const hikeHours = stop.hike?.hoursMax;

          return (
            <div
              key={stop.id}
              className="add-stop-row"
              // Se StopHoverCard: kortet lukker seg selv utenfor en slik node.
              data-hover-source=""
              onMouseEnter={() => onHover?.(stop)}
              onMouseLeave={() => onHover?.(null)}
            >
              <button className="add-stop-item" onClick={() => onAdd(stop.id)}>
                <span
                  className="legend-dot"
                  style={{ background: TAG_COLOR[stop.tags[0]] }}
                  aria-hidden="true"
                />
                <span className="add-stop-main">
                  <span className="add-stop-name">
                    {stop.name}
                    {/* Typen, som er det raskeste svaret på «hva er dette». */}
                    <span className="add-stop-tag">{t.tagName[stop.tags[0]]}</span>
                  </span>
                  {/*
                    Én linje beskrivelse. Kuttet av CSS framfor i JavaScript, så
                    hele teksten er der for skjermlesere og utskrift.
                  */}
                  <span className="add-stop-blurb">{stop.blurb}</span>
                  <span className="add-stop-facts">
                    <span>
                      <Mountain size={10} /> {stop.elevationM} {t.unitMasl}
                    </span>
                    <span>
                      <Clock size={10} /> {t.addStopDwell(stop.dwellHours)}
                    </span>
                    <span>~{formatHours(stop.hoursFromStart, lang)}</span>
                    {hikeHours !== undefined && <span>{t.addStopHike(hikeHours)}</span>}
                    {winterNote && (
                      <span className="add-stop-winter">
                        <Snowflake size={10} /> {t.winterStatus[winterNote.status]}
                      </span>
                    )}
                  </span>
                </span>
                <Plus size={14} className="add-stop-plus" />
              </button>

              {onInspect && (
                <button
                  className="add-stop-info"
                  onClick={() => onInspect(stop)}
                  aria-label={t.addStopAbout(stop.name)}
                  title={t.addStopAbout(stop.name)}
                >
                  <Info size={14} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {!showAll && nearby.length > 0 && nearby.length < alternatives.length && (
        <button className="link-button" onClick={() => setShowAll(true)}>
          {t.showAll(alternatives.length)}
        </button>
      )}
    </div>
  );
}
